/*
Purpose: Upload new evidence (videos, images, documents, audio) into a casefile.
Audience: Users adding files to investigate.
What it does:
- Handles file selection, validation, and upload to Firebase Storage
- Writes metadata documents to Firestore under the casefile's evidence collection
Interplay:
- Uploaded items appear in EvidenceList/CasefileDetail and open in EvidenceModal
*/
import React, {useState, useEffect} from 'react';
import {db, storage} from '../../firebase';
import {useAuth} from '../../hooks/useAuth';
import {ref, uploadBytesResumable, getDownloadURL} from 'firebase/storage';
import {collection, addDoc, serverTimestamp} from 'firebase/firestore';
import '../../styles/EvidenceUploader.css';

const EvidenceUploader = ({casefileId, onUploadComplete, onClose}) => {
    const { user, requireAuth } = useAuth();
    const [file, setFile] = useState(null); // Changed from videoFile to generic file
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [editableName, setEditableName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);

    useEffect(() => {
        setFile(null);
        setProgress(0);
        setError('');
        setIsUploading(false);
        setUploadSuccess(false);
        setEditableName('');
        setIsEditingName(false);
    }, [casefileId]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setUploadSuccess(false);

        if (!selectedFile) {
            setFile(null);
            setError('');
            return;
        }

        // Expanded validation for multiple file types
        const validTypes = ['video/', 'image/', 'application/pdf', 'audio/'];
        if (!validTypes.some(type => selectedFile.type.startsWith(type))) {
            setError('Unsupported format. Please upload a video, image, PDF, or audio file.');
            setFile(null);
            return;
        }

        const maxSize = 100 * 1024 * 1024; // 100MB
        if (selectedFile.size > maxSize) {
            setError('File size must be less than 100MB.');
            setFile(null);
            return;
        }

        setFile(selectedFile);
        setError('');
        
        // Set initial editable name (file name without extension)
        const nameWithoutExt = selectedFile.name.substring(0, selectedFile.name.lastIndexOf('.')) || selectedFile.name;
        setEditableName(nameWithoutExt);
        setIsEditingName(false);
    };
    
    // --- Helper function to determine evidence type from MIME type ---
    const getEvidenceType = (mimeType) => {
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('audio/')) return 'audio';
        if (mimeType.includes('pdf')) return 'document';
        return 'document'; // Default for other application types
    };

    // --- Handle name edit toggle ---
    const handleEditNameClick = () => {
        setIsEditingName(true);
    };

    const handleNameChange = (e) => {
        setEditableName(e.target.value);
    };

    const handleNameBlur = () => {
        setIsEditingName(false);
        // Trim whitespace
        setEditableName(prev => prev.trim() || 'Untitled');
    };

    const handleNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur(); // Trigger blur to save
        }
    };

    const handleUpload = () => {
        if (!requireAuth(() => {}, 'Authentication required. Please sign in.')) {
            setError('Authentication required. Please sign in.');
            return;
        }
        if (!file) {
            setError('Please select a file.');
            return;
        }

        setIsUploading(true);
        setProgress(0);
        setError('');
        setUploadSuccess(false);

        const storageRef = ref(storage, `evidence/${casefileId}/${user.uid}-${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const prog = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                );
                setProgress(prog);
            },
            (err) => {
                console.error("Upload error:", err);
                setError(`Upload failed: ${err.message || 'Please try again.'}`);
                setIsUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then(async (url) => {
                    const fileRef = uploadTask.snapshot.ref;
                    const bucket = fileRef.bucket;
                    const fullPath = fileRef.fullPath;
                    
                    // --- FIX: Construct the full GCS URI ---
                    const gcsUri = `gs://${bucket}/${fullPath}`;

                    // Get the file extension from the original file
                    const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
                    const displayName = editableName.trim() ? `${editableName.trim()}${fileExtension}` : file.name;

                    await addDoc(collection(db, 'casefiles', casefileId, 'evidence'), {
                        type: getEvidenceType(file.type),
                        casefileId: casefileId,
                        downloadURL: url,
                        fileName: displayName, // Use the edited name with extension
                        filePath: gcsUri, // <-- CORRECTED: Saves the full gs:// path
                        contentType: file.type,
                        fileSize: file.size,
                        createdAt: serverTimestamp(),
                        createdBy: user.uid,
                        uploaderUid: user.uid,
                        // Initialize other fields as needed
                    });
                    
                    setIsUploading(false);
                    setUploadSuccess(true);
                    setTimeout(() => {
                        onUploadComplete();
                    }, 1500);
                });
            }
        );
    };

    return (
        <div className="evidence-uploader-container">
            <div className="uploader-header">
                <h3>Upload New Evidence</h3>
                <button onClick={onClose} className="uploader-close-btn" aria-label="Close">
                    &times;
                </button>
            </div>
            
            <input
                type="file"
                id="evidence-file-upload" // Generic ID
                accept="video/*,image/*,audio/*,.pdf" // Accept more types
                onChange={handleFileChange}
                disabled={isUploading}
                style={{display: 'none'}}
            />
            <label htmlFor="evidence-file-upload" className="upload-label">
                Select File
            </label>

            {file && (
                <div className="file-name-editor">
                    <span className="file-label">Evidence Name:</span>
                    {isEditingName ? (
                        <input
                            type="text"
                            value={editableName}
                            onChange={handleNameChange}
                            onBlur={handleNameBlur}
                            onKeyDown={handleNameKeyDown}
                            className="name-edit-input"
                            autoFocus
                            disabled={isUploading}
                        />
                    ) : (
                        <span className="file-name-display">
                            {editableName}
                            <button 
                                onClick={handleEditNameClick} 
                                className="edit-name-btn"
                                disabled={isUploading}
                                aria-label="Edit evidence name"
                                title="Edit evidence name"
                            >
                                ✏️
                            </button>
                        </span>
                    )}
                    <span className="file-extension-hint">
                        {file.name.substring(file.name.lastIndexOf('.'))}
                    </span>
                </div>
            )}

            {isUploading && (
                <div className="progress-container">
                    <div className="progress-bar" style={{width: `${progress}%`}}></div>
                </div>
            )}
            
            {error && <p className="status-message error-message">{error}</p>}
            {uploadSuccess && <p className="status-message success-message">Upload Complete!</p>}
            
            <button onClick={handleUpload} disabled={isUploading || !file} className="upload-button">
                {isUploading ? `Uploading... ${progress}%` : 'Upload and Submit'}
            </button>
        </div>
    );
};

export default EvidenceUploader;